import { Accept, useDropzone } from 'react-dropzone';

import { useFeature } from '@hooks/useFeature';
import { useToaster } from '@stores/toaster';

import { useBulkUploadActions, useBulkUploadState } from '../../context';
import { useCreateRecordingItems } from '../../modals/useCreateRecordingItems';

export const useDrop = (supportedFileTypes: Accept) => {
  const { addItems } = useBulkUploadActions();
  const { studyId } = useBulkUploadState();

  const hasRepoFiles = useFeature('repo_files');

  const showToast = useToaster();

  const { createItems } = useCreateRecordingItems({ studyId });

  const isFileAllowed = (file: File) => {
    const mimeType = file.type;
    const hasValidMimeType = Object.keys(supportedFileTypes).includes(mimeType);
    const hasValidExtension = Object.values(supportedFileTypes).some((extensions) =>
      extensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    );

    return hasValidMimeType || hasValidExtension;
  };

  const handleDrop = (files: File[]) => {
    const badFiles = files.filter((f) => !isFileAllowed(f));
    const goodFiles = files.filter((f) => isFileAllowed(f));

    if (badFiles.length > 0) {
      let heading = "We can't upload that file.";

      if (badFiles.length > 1) {
        heading = "We can't upload some of those files!";
      }

      const badExtensions = Array.from(
        new Set(badFiles.map((f) => f.name.split('.').pop()?.toUpperCase()).filter(Boolean) as string[])
      ).join(', ');

      showToast({
        heading,
        text: `The following file types are not supported: ${badExtensions}.`,
        icon: 'error',
        timeoutSeconds: 8
      });
    }

    if (goodFiles.length > 0) {
      createItems(goodFiles).then(addItems);
    }
  };

  return useDropzone({
    onDrop: handleDrop,
    noClick: true,
    accept: hasRepoFiles ? undefined : supportedFileTypes
  });
};
